import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { t } from '@/lib/i18n';
import type { RorosCanvasBlockDTO, RorosCanvasItemDTO } from '@/types/roros/canvases';
import { BrainCircuit, Plus, RefreshCw } from 'lucide-react';
import { CanvasItem } from './CanvasItem';

interface CanvasBlockProps {
  blockKey: string;
  title: string;
  block: RorosCanvasBlockDTO | undefined;
  canEdit: boolean;
  isSaving: boolean;
  onItemsChange: (items: RorosCanvasItemDTO[]) => void;
  onRegenerate?: () => void;
  isGenerating?: boolean;
  showAiConfigureAffordance?: boolean;
  className?: string;
}

function newId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function orderItems(items: RorosCanvasItemDTO[]) {
  return items.map((item, index) => ({ ...item, sortOrder: index }));
}

export function CanvasBlock({
  blockKey,
  title,
  block,
  canEdit,
  isSaving,
  onItemsChange,
  onRegenerate,
  isGenerating = false,
  showAiConfigureAffordance = false,
  className,
}: CanvasBlockProps) {
  const [newText, setNewText] = useState('');
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [emptyAttempted, setEmptyAttempted] = useState(false);
  const draggedIdRef = useRef<string | null>(null);

  const items = [...(block?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  function addItem() {
    const text = newText.trim();
    if (!text) {
      setEmptyAttempted(true);
      return;
    }
    setEmptyAttempted(false);
    setNewText('');
    onItemsChange(orderItems([...items, { id: newId(), text, source: 'user', sortOrder: items.length }]));
  }

  function editItem(itemId: string, text: string) {
    onItemsChange(orderItems(items.map((item) => item.id === itemId ? { ...item, text, source: 'user' } : item)));
  }

  function deleteItem(itemId: string) {
    onItemsChange(orderItems(items.filter((item) => item.id !== itemId)));
  }

  function dropItem(targetId: string) {
    const activeDraggedId = draggedIdRef.current ?? draggedId;
    if (!activeDraggedId || activeDraggedId === targetId) return;
    const from = items.findIndex((item) => item.id === activeDraggedId);
    const to = items.findIndex((item) => item.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    draggedIdRef.current = null;
    setDraggedId(null);
    setDropTargetId(null);
    onItemsChange(orderItems(next));
  }

  function startDrag(itemId: string) {
    draggedIdRef.current = itemId;
    setDraggedId(itemId);
  }

  function endDrag() {
    draggedIdRef.current = null;
    setDraggedId(null);
    setDropTargetId(null);
  }

  return (
    <section
      className={`flex min-h-[13rem] flex-col rounded-xl border border-border bg-card p-3 shadow-sm ${className ?? ''}`}
      data-testid={`canvas-block-${blockKey}`}
      data-canvas-block={blockKey}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <div className="flex items-center gap-2">
          {canEdit && onRegenerate && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRegenerate}
              disabled={isSaving || isGenerating}
              data-testid={`canvas-regenerate-${blockKey}`}
              aria-label={t('Regenerate block')}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              {t('Regenerate')}
            </Button>
          )}
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {items.length}
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2">
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-sm text-muted-foreground" data-testid={`canvas-empty-${blockKey}`}>
            <p>{t('No items yet. Add a sticky note to this block.')}</p>
            {showAiConfigureAffordance && (
              <Link
                to="/roros/settings?tab=ai"
                className="mt-2 inline-flex items-center gap-1 text-primary-600 underline-offset-4 hover:underline"
                data-testid={`canvas-ai-configure-${blockKey}`}
              >
                <BrainCircuit className="h-4 w-4" />
                {t('Suggest with AI — configure AI provider')}
              </Link>
            )}
          </div>
        ) : (
          items.map((item) => (
            <CanvasItem
              key={item.id}
              item={item}
              canEdit={canEdit}
              isDragging={item.id === draggedId || item.id === dropTargetId}
              onEdit={(text) => editItem(item.id, text)}
              onDelete={() => deleteItem(item.id)}
              onDragStart={() => startDrag(item.id)}
              onDragOver={() => setDropTargetId(item.id)}
              onDrop={() => dropItem(item.id)}
              onDragEnd={endDrag}
            />
          ))
        )}
      </div>

      {canEdit && (
        <div className="mt-3 space-y-2 roros-canvas-no-print">
          <Input
            value={newText}
            onChange={(event) => {
              setNewText(event.target.value);
              if (event.target.value.trim()) setEmptyAttempted(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') addItem();
            }}
            placeholder={t('Add a sticky note')}
            aria-label={t('Add canvas item')}
            disabled={isSaving}
            error={emptyAttempted ? t('Enter item text before adding.') : undefined}
          />
          <Button size="sm" variant="outline" onClick={addItem} disabled={isSaving} data-testid={`canvas-add-${blockKey}`}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('Add')}
          </Button>
        </div>
      )}
    </section>
  );
}
